import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, Sparkles } from 'lucide-react';

export const TOUR_STORAGE_KEY = 'lexorbit_tour_seen';

const STEPS = [
  {
    target: null,
    title: 'Bienvenido a LexOrbit',
    text: 'Te mostramos un recorrido rápido por las secciones principales del sistema. Podés salir cuando quieras con el botón "Saltar".',
  },
  { target: 'nav-dashboard', title: 'Panel', text: 'Tu pantalla de inicio: métricas clave, gráficos y alertas de audiencias y vencimientos.' },
  { target: 'nav-clientes', title: 'Clientes', text: 'Administrá los datos de tus clientes, personas físicas y jurídicas, y vinculá expedientes a cada uno.' },
  { target: 'nav-expedientes', title: 'Expedientes', text: 'El núcleo del sistema: causas judiciales, evidencias, movimientos y datos financieros de cada caso.' },
  { target: 'nav-carpetas', title: 'Carpetas', text: 'Agrupá varios expedientes que pertenecen a una misma causa o asunto general.' },
  { target: 'nav-agenda', title: 'Agenda', text: 'Tu calendario judicial, con sincronización opcional con Google Calendar.' },
  { target: 'nav-facturacion', title: 'Facturación', text: 'Gestioná honorarios, cuotas con interés y el estado de cobranza de cada cliente.' },
  { target: 'nav-vault', title: 'Bóveda', text: 'Repositorio central y seguro de todos los archivos de tus expedientes.' },
  { target: 'nav-documentos', title: 'Documentos', text: 'Generá escritos a partir de plantillas y guardalos directo en la bóveda.' },
  { target: 'nav-asistente', title: 'Asistente IA', text: 'Chat con inteligencia artificial especializada en derecho argentino, para consultas y redacción.' },
  { target: 'nav-analisis-casos', title: 'Análisis de Casos', text: 'Analizá expedientes, corregí documentos y redactá escritos con ayuda de IA. Siempre verificá el resultado antes de usarlo.' },
  { target: 'nav-sincronizacion-judicial', title: 'Sincronización Judicial', text: 'Conectá los portales judiciales (PJN, SCBA-MEV, etc.) para traer movimientos automáticamente.' },
  { target: 'nav-reportes', title: 'Reportes', text: 'Analizá facturación, causas, pagos, vencimientos, morosos y productividad del estudio.' },
  { target: 'nav-suscripcion', title: 'Mi suscripción', text: 'Consultá el estado de tu plan, su uso y activá o renová tu suscripción.' },
  { target: 'notificaciones', title: 'Notificaciones', text: 'La campana te avisa sobre audiencias próximas y honorarios por vencer o vencidos.' },
  { target: 'ayuda', title: 'Ayuda', text: 'Desde aquí podés volver a ver esta guía o descargar el manual de usuario completo en PDF.' },
];

export default function GuideTour({ onClose, forceExpand }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => { forceExpand?.(true); return () => forceExpand?.(false); }, [forceExpand]);

  useLayoutEffect(() => {
    const current = STEPS[step];
    if (!current.target) { setRect(null); return; }

    const update = () => {
      const el = document.querySelector(`[data-tour="${current.target}"]`);
      if (!el) { setRect(null); return; }
      // Scrollear PRIMERO para que el elemento quede en su posición final,
      // y recién entonces medir; si no, el recuadro queda en la posición vieja
      // (caía sobre "Cerrar sesión"/"Contraer menú" para los ítems del fondo).
      el.scrollIntoView?.({ block: 'nearest' });
      requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      });
    };

    const t = setTimeout(update, 80);
    window.addEventListener('resize', update);
    return () => { clearTimeout(t); window.removeEventListener('resize', update); };
  }, [step]);

  const finish = () => {
    try { localStorage.setItem(TOUR_STORAGE_KEY, 'true'); } catch {}
    onClose?.();
  };

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const pad = 6;
  const box = rect ? {
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  } : null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let cardStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  if (box) {
    const cardW = 320;
    let left = box.left + box.width + 16;
    let top = box.top;
    if (left + cardW > vw - 16) left = Math.max(16, box.left - cardW - 16);
    if (top + 220 > vh - 16) top = Math.max(16, vh - 236);
    cardStyle = { top, left, transform: 'none' };
  }

  return (
    <div className="fixed inset-0 z-[200]">
      {/* Dim overlay with spotlight cutout */}
      {box ? (
        <>
          <div className="fixed bg-black/65 transition-all duration-300" style={{ top: 0, left: 0, width: '100%', height: Math.max(0, box.top) }} />
          <div className="fixed bg-black/65 transition-all duration-300" style={{ top: box.top + box.height, left: 0, width: '100%', height: Math.max(0, vh - (box.top + box.height)) }} />
          <div className="fixed bg-black/65 transition-all duration-300" style={{ top: box.top, left: 0, width: Math.max(0, box.left), height: box.height }} />
          <div className="fixed bg-black/65 transition-all duration-300" style={{ top: box.top, left: box.left + box.width, width: Math.max(0, vw - (box.left + box.width)), height: box.height }} />
          <div className="fixed rounded-lg border-2 border-[#C9A44C] pointer-events-none transition-all duration-300" style={{ top: box.top, left: box.left, width: box.width, height: box.height, boxShadow: '0 0 0 4px rgba(201,164,76,0.15)' }} />
        </>
      ) : (
        <div className="fixed inset-0 bg-black/70" />
      )}

      {/* Tooltip / card */}
      <div ref={cardRef} className="fixed z-[210] w-[320px] bg-[#0E0E0E] border border-[rgba(201,164,76,0.25)] rounded-xl shadow-2xl p-4"
        style={cardStyle}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 text-[#C9A44C]">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-semibold">{current.title}</span>
          </div>
          <button onClick={finish} className="text-[#666] hover:text-[#F8F6F0] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[#B8B8B8] text-xs leading-relaxed mb-4">{current.text}</p>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-[#555]">{step + 1} / {STEPS.length}</span>
          <div className="flex items-center gap-2">
            <button onClick={finish} className="text-[11px] text-[#888] hover:text-[#F8F6F0] transition-colors px-2 py-1.5">
              Saltar
            </button>
            {step > 0 && (
              <button onClick={() => setStep((s) => s - 1)}
                className="flex items-center gap-1 text-[11px] text-[#A0A0A0] hover:text-[#F8F6F0] border border-white/10 rounded-lg px-2.5 py-1.5 transition-colors">
                <ChevronLeft className="w-3.5 h-3.5" /> Atrás
              </button>
            )}
            <button onClick={() => (isLast ? finish() : setStep((s) => s + 1))}
              className="flex items-center gap-1 text-[11px] font-semibold text-[#0E0E0E] rounded-lg px-3 py-1.5 transition-colors"
              style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
              {isLast ? 'Listo' : 'Siguiente'} {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
